import CheckOutlined from '@ant-design/icons/CheckOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import { Button, Checkbox, message, Tag } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AccessControlAction from '../../components/AccessControlAction'
import { EmptyText, WordBreak } from '../../components/Common/CellText'
import RichEditor from '../../components/RichEditor/RichEditor'
import { ACL_ACTION_TYPE, DATE_FORMAT_SLASH, MM_YYYY } from '../../constants'
import ListHistoryLogModal from '../ListHistoryLog/ListHistoryLogModal'
import { StatusTag } from './MissionDetailPageStyled'

const MissionDetailUpdate = props => {
  const {
    resultTask,
    contentTask,
    taskStore,
    files,
    missionId,
    setIsUpdateMissionDetail,
    isUpdateMissionDetail,
    fetchTaskDetail,
  } = props

  const taskCompletePercentageStr =
    taskStore.taskDetail?.contentTask?.ket_qua?.phan_tram_hoan_thanh
  const percentComplete = taskCompletePercentageStr
    ? parseInt(taskCompletePercentageStr, 10)
    : 0

  const [percentCompleteUpdate, setPercentCompleteUpdate] = useState(0)
  const [editorContentResult, setEditorContentResult] = useState('')
  const [editorKienNghi, setEditorKienNghi] = useState('')
  const [editorCanhBao, setEditorCanhBao] = useState('')
  const [editorDiemNghen, setEditorDiemNghen] = useState('')
  const [editorDeXuat, setEditorDeXuat] = useState('')
  const [isUpdate, setIsUpdate] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isVisibleHistory, setIsVisibleHistory] = useState(false)
  const [documentHistory, setDocumentHistory] = useState({})

  const CONTENT_RESULT_REF = useRef(null)
  const KIEN_NGHI_REF = useRef(null)
  const CANH_BAO_REF = useRef(null)
  const DIEM_NGHEN_REF = useRef(null)
  const DE_XUAT_REF = useRef(null)

  useEffect(() => {
    setPercentCompleteUpdate(
      resultTask.phan_tram_hoan_thanh
        ? parseInt(resultTask.phan_tram_hoan_thanh, 10)
        : 0
    )
    setEditorContentResult(resultTask.noi_dung_ket_qua_giao || '')
    setEditorKienNghi(resultTask.kien_nghi || '')
    setEditorCanhBao(resultTask.canh_bao || '')
    setEditorDiemNghen(resultTask.diem_nghen || '')
    setEditorDeXuat(resultTask.de_xuat || '')
  }, [resultTask, isUpdate])

  const handleUpdateMission = useCallback(async () => {
    const currentNoiDungKetQua = CONTENT_RESULT_REF.current
      ? CONTENT_RESULT_REF.current.editor.getData()
      : ''
    const currentKienNghi = KIEN_NGHI_REF.current
      ? KIEN_NGHI_REF.current.editor.getData()
      : ''
    const currentCanhBao = CANH_BAO_REF.current
      ? CANH_BAO_REF.current.editor.getData()
      : resultTask.canh_bao || ''
    const currentDiemNghen = DIEM_NGHEN_REF.current
      ? DIEM_NGHEN_REF.current.editor.getData()
      : resultTask.diem_nghen || ''
    const currentDeXuat = DE_XUAT_REF.current
      ? DE_XUAT_REF.current.editor.getData()
      : resultTask.de_xuat || ''
    const currentPhanTram = percentCompleteUpdate

    const initialPhanTram = resultTask.phan_tram_hoan_thanh
      ? parseInt(resultTask.phan_tram_hoan_thanh, 10)
      : 0
    const initialNoiDungKetQua = resultTask.noi_dung_ket_qua_giao || ''
    const initialKienNghi = resultTask.kien_nghi || ''
    const initialCanhBao = resultTask.canh_bao || ''
    const initialDiemNghen = resultTask.diem_nghen || ''
    const initialDeXuat = resultTask.de_xuat || ''

    if (
      currentPhanTram === initialPhanTram &&
      currentNoiDungKetQua === initialNoiDungKetQua &&
      currentKienNghi === initialKienNghi &&
      currentCanhBao === initialCanhBao &&
      currentDiemNghen === initialDiemNghen &&
      currentDeXuat === initialDeXuat
    ) {
      message.info('Không có giá trị nào thay đổi !!')
      return
    }

    try {
      const submitData = {
        kien_nghi: currentKienNghi,
        noi_dung_ket_qua: currentNoiDungKetQua,
        canh_bao: currentCanhBao,
        diem_nghen: currentDiemNghen,
        de_xuat: currentDeXuat,
        ty_le_hoan_thanh: currentPhanTram,
        files: files.map(file => file.file_id),
      }
      setIsUpdating(true)
      await taskStore.updateTask(missionId, submitData)

      if (setIsUpdateMissionDetail) {
        setIsUpdateMissionDetail(!isUpdateMissionDetail)
      }

      await Promise.all([
        taskStore.getTaskList(),
        taskStore.getTaskDetail(missionId),
      ])

      setIsUpdate(false)
      if (fetchTaskDetail) {
        fetchTaskDetail()
      }

      message.success('Cập nhật nhiệm vụ thành công!')
    } catch (err) {
      message.error(err?.vi || 'Đã có lỗi xảy ra')
    } finally {
      setIsUpdating(false)
    }
  }, [
    percentCompleteUpdate,
    resultTask,
    files,
    missionId,
    taskStore,
    setIsUpdateMissionDetail,
    isUpdateMissionDetail,
    fetchTaskDetail,
  ])

  const handleToggleHistory = useCallback(() => {
    setDocumentHistory({ code: missionId })
    setIsVisibleHistory(prev => !prev)
  }, [missionId])

  const handleCloseHistory = useCallback(() => {
    setIsVisibleHistory(false)
  }, [])

  const buttonGroupStyle = useMemo(
    () => ({
      margin: '20px 0',
      display: 'flex',
      gap: 8,
      justifyContent: 'flex-end',
    }),
    []
  )

  return (
    <>
      <dt>Trạng thái</dt>
      <dd>
        {!isUpdate ? (
          <StatusTag>
            {percentComplete === 100 ? (
              <Tag color="green">Hoàn thành</Tag>
            ) : (
              <Tag color="red">Chưa hoàn thành</Tag>
            )}
          </StatusTag>
        ) : (
          <Checkbox
            checked={percentCompleteUpdate === 100}
            onChange={e =>
              setPercentCompleteUpdate(e.target.checked ? 100 : 0)
            }>
            Hoàn thành
          </Checkbox>
        )}
      </dd>

      <dt>Nội dung kết quả</dt>
      {!isUpdate ? (
        <dd>
          {resultTask.noi_dung_ket_qua_giao ? (
            WordBreak(resultTask.noi_dung_ket_qua_giao, true)
          ) : (
            <EmptyText>Không có nội dung kết quả</EmptyText>
          )}
        </dd>
      ) : (
        <div style={{ width: '100%' }}>
          <RichEditor
            EDITOR_REF={CONTENT_RESULT_REF}
            placeholder="Nhập nội dung kết quả..."
            editorContent={editorContentResult}
          />
        </div>
      )}

      <dt>Kiến nghị</dt>
      {!isUpdate ? (
        <dd>
          {resultTask.kien_nghi ? (
            WordBreak(resultTask.kien_nghi, true)
          ) : (
            <EmptyText>Không có kiến nghị</EmptyText>
          )}
        </dd>
      ) : (
        <div style={{ width: '100%' }}>
          <RichEditor
            EDITOR_REF={KIEN_NGHI_REF}
            placeholder="Nhập nội dung kết quả..."
            editorContent={editorKienNghi}
          />
        </div>
      )}

      <AccessControlAction
        aclActionType={ACL_ACTION_TYPE.canh_bao__MISSION}
        fallback={null}>
        {!isUpdate ? (
          <>
            <dt>Cảnh báo</dt>
            <dd>
              {resultTask.canh_bao ? (
                WordBreak(resultTask.canh_bao, true)
              ) : (
                <EmptyText>Không có cảnh báo</EmptyText>
              )}
            </dd>
          </>
        ) : (
          <>
            <dt>Cảnh báo</dt>
            <div style={{ width: '100%' }}>
              <RichEditor
                EDITOR_REF={CANH_BAO_REF}
                placeholder="Nhập cảnh báo..."
                editorContent={editorCanhBao}
              />
            </div>
          </>
        )}
      </AccessControlAction>

      <AccessControlAction
        aclActionType={ACL_ACTION_TYPE.diem_nghen__MISSION}
        fallback={null}>
        {!isUpdate ? (
          <>
            <dt>Điểm nghẽn</dt>
            <dd>
              {resultTask.diem_nghen ? (
                WordBreak(resultTask.diem_nghen, true)
              ) : (
                <EmptyText>Không có điểm nghẽn</EmptyText>
              )}
            </dd>
          </>
        ) : (
          <>
            <dt>Điểm nghẽn</dt>
            <div style={{ width: '100%' }}>
              <RichEditor
                EDITOR_REF={DIEM_NGHEN_REF}
                placeholder="Nhập điểm nghẽn..."
                editorContent={editorDiemNghen}
              />
            </div>
          </>
        )}
      </AccessControlAction>
      <AccessControlAction
        aclActionType={ACL_ACTION_TYPE.de_xuat__MISSION}
        fallback={null}>
        {!isUpdate ? (
          <>
            <dt>Đề xuất</dt>
            <dd>
              {resultTask.de_xuat ? (
                WordBreak(resultTask.de_xuat, true)
              ) : (
                <EmptyText>Không có đề xuất</EmptyText>
              )}
            </dd>
          </>
        ) : (
          <>
            <dt>Đề xuất</dt>
            <div style={{ width: '100%' }}>
              <RichEditor
                EDITOR_REF={DE_XUAT_REF}
                placeholder="Nhập đề xuất..."
                editorContent={editorDeXuat}
              />
            </div>
          </>
        )}
      </AccessControlAction>
      <dt>Hiển thị vào tháng</dt>
      <dd>
        {contentTask?.display_date ? (
          `Tháng ${moment(contentTask.display_date).format(MM_YYYY)}`
        ) : (
          <EmptyText>Không rõ</EmptyText>
        )}
      </dd>

      <dt>Ngày tạo</dt>
      <dd>
        {contentTask?.created_at ? (
          moment(contentTask.created_at).format(DATE_FORMAT_SLASH)
        ) : (
          <EmptyText>Không rõ</EmptyText>
        )}
      </dd>

      <dt>Loại nhiệm vụ</dt>
      <dd>
        {contentTask?.tags && contentTask.tags.length > 0 ? (
          contentTask.tags.map(tag => tag.name).join(', ')
        ) : (
          <EmptyText>Chưa phân loại</EmptyText>
        )}
      </dd>

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexWrap: 'nowrap',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Button onClick={handleToggleHistory}>Lịch sử</Button>

        <div style={buttonGroupStyle}>
          {isUpdate ? (
            <>
              <Button
                icon={
                  <CloseOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                danger
                onClick={() => setIsUpdate(false)}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={isUpdating}
                onClick={handleUpdateMission}
                icon={
                  <CheckOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }>
                Lưu lại
              </Button>
            </>
          ) : (
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.cap_nhat__MISSION}>
              <Button
                onClick={() => setIsUpdate(true)}
                type="primary"
                icon={
                  <EditOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }>
                Cập nhật nhiệm vụ
              </Button>
            </AccessControlAction>
          )}
        </div>

        <ListHistoryLogModal
          type="TASK"
          isVisible={isVisibleHistory}
          taskInfo={documentHistory}
          handleCloseHistory={handleCloseHistory}
        />
      </div>
    </>
  )
}

export default inject(
  'taskStore',
  'loadingAnimationStore',
  'authenticationStore',
  'fileStore',
  'selectPeopleStore'
)(observer(MissionDetailUpdate))
